/**
 * People presentation used everywhere a person appears (recipient chips, suggestions, address
 * book, reading pane): avatar with photo and coloured-initials fallback, and a display name that
 * shows the address on hover/focus (DESIGN.md T11).
 */
import { UsersIcon } from 'lucide-react';
import type { ReactNode } from 'react';
import { Avatar, AvatarFallback, AvatarImage, initials } from '../components/misc';
import { Tooltip, TooltipContent, TooltipTrigger } from '../components/tooltip';
import { cn } from '../lib/utils';
import { avatarTone } from './recipients';

/** Tinted chart tokens: readable text in both themes, never raw colours (DESIGN.md §3). */
const TONES = [
  'bg-chart-1/25 text-foreground',
  'bg-chart-2/25 text-foreground',
  'bg-chart-3/30 text-foreground',
  'bg-chart-4/25 text-foreground',
  'bg-chart-5/25 text-foreground',
] as const;

const SIZES = { xs: 'size-5', sm: 'size-6', md: 'size-8', lg: 'size-10' } as const;
const TEXT = { xs: 'text-[0.5625rem]', sm: 'text-[0.625rem]', md: 'text-xs', lg: 'text-sm' } as const;

export interface PersonAvatarProps {
  name?: string | null | undefined;
  email: string;
  /** Photo URL (server answers 404 when there is none → initials). */
  photoUrl?: string | undefined;
  group?: boolean;
  size?: keyof typeof SIZES;
  className?: string;
}

export function PersonAvatar({ name, email, photoUrl, group = false, size = 'md', className }: PersonAvatarProps) {
  const tone = TONES[avatarTone(email || name || '?')];
  return (
    <Avatar aria-hidden className={cn(SIZES[size], className)}>
      {photoUrl && !group && <AvatarImage src={photoUrl} alt="" />}
      <AvatarFallback className={cn('font-medium', TEXT[size], tone)}>
        {group ? <UsersIcon className="size-3/5" /> : initials(name || email) || '?'}
      </AvatarFallback>
    </Avatar>
  );
}

export interface PersonNameProps {
  name?: string | null | undefined;
  email: string;
  className?: string;
  /** Rendered before the name (e.g. an sr-only "From:" prefix). */
  prefix?: ReactNode;
  /**
   * Item id inside a `ContextMenuArea` (DESIGN.md §18): the name becomes a button whose click,
   * right-click, Shift+F10 and long-press open the address actions.
   */
  contextId?: string;
}

const focusRing = 'rounded-sm outline-none focus-visible:ring-3 focus-visible:ring-ring/50';

/** Display name with the address in a tooltip (hover and keyboard focus). Without a name: the address. */
export function PersonName({ name, email, className, prefix, contextId }: PersonNameProps) {
  const withAddress = Boolean(email && name && name !== email);
  const content = (
    <>
      {prefix}
      {withAddress ? name : name || email}
      {withAddress && <span className="sr-only"> &lt;{email}&gt;</span>}
    </>
  );
  const element =
    contextId !== undefined ? (
      <button type="button" data-context-id={contextId} className={cn(focusRing, 'touch-callout-none text-left hover:underline', className)}>
        {content}
      </button>
    ) : withAddress ? (
      <span tabIndex={0} className={cn(focusRing, className)}>
        {content}
      </span>
    ) : (
      <span className={className}>{content}</span>
    );
  if (!withAddress) return element;
  return (
    <Tooltip>
      <TooltipTrigger asChild>{element}</TooltipTrigger>
      <TooltipContent>{email}</TooltipContent>
    </Tooltip>
  );
}
